/**
 * FeedbackWidget — #324
 *
 * Top-nav button + popover for alpha-cohort feedback. Click the
 * Feedback button, pick a category, type a message, Send. The service
 * auto-collects pageUrl / userAgent / userId / appVersion.
 *
 * Popover (not modal) so it doesn't disrupt the page the advisor is
 * actually trying to give feedback about. Matches the RemindersWidget
 * pattern (#298).
 */

import { useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import feedbackService from '../../services/FeedbackService';

const CATEGORIES = [
  { value: 'bug', label: 'Bug' },
  { value: 'idea', label: 'Idea' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
];

const SUCCESS_AUTO_CLOSE_MS = 1500;

export default function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sentAt, setSentAt] = useState(null); // truthy while the "Sent!" badge is showing
  const popoverRef = useRef(null);

  // Close on outside click (popover pattern from RemindersWidget — #298)
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Auto-close after the "Sent!" badge has been on screen long enough
  // for the user to register success.
  useEffect(() => {
    if (!sentAt) return undefined;
    const t = setTimeout(() => {
      setIsOpen(false);
      setSentAt(null);
      setMessage('');
      setCategory('bug');
    }, SUCCESS_AUTO_CLOSE_MS);
    return () => clearTimeout(t);
  }, [sentAt]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!message.trim() || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await feedbackService.submitFeedback({ category, message });
      setSentAt(Date.now());
    } catch (err) {
      // Keep the draft text + popover open so the user can retry without
      // re-typing. Surface the error so they know something went wrong.
      setError(err?.message || 'Could not send feedback — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setError(null);
    setSentAt(null);
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Send feedback"
        aria-expanded={isOpen}
        className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
      >
        <MessageSquare className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
          role="dialog"
          aria-label="Send feedback"
        >
          {sentAt ? (
            <div className="p-6 text-center">
              <div className="text-green-700 text-base font-medium">Sent! ✓</div>
              <p className="text-xs text-gray-500 mt-1">
                Thanks &mdash; we read every one of these during alpha.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              <div>
                <label
                  htmlFor="feedback-category"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Type
                </label>
                <select
                  id="feedback-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="feedback-message"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  What&apos;s on your mind?
                </label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  placeholder="Screenshots welcome — just paste or describe."
                  className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={5000}
                  autoFocus
                />
              </div>

              {error && (
                <div
                  role="alert"
                  className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5"
                >
                  {error}
                </div>
              )}

              <p className="text-[10px] text-gray-400">
                We&apos;ll see this page URL + your browser version with your message.
                {category === 'bug' &&
                  ' For bug reports, recent console errors are attached automatically (secrets redacted).'}
              </p>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="text-sm text-gray-600 hover:text-gray-900 px-2 py-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!message.trim() || submitting}
                  className="text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  {submitting ? (
                    <>
                      <span
                        className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"
                        aria-hidden="true"
                      />
                      Sending…
                    </>
                  ) : (
                    'Send'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
